import { useState, useEffect } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { useParams, useNavigate } from "react-router-dom";
import StateMap from "../StateMap";
import SchoolsMap from "../../components/SchoolsMap";
import StatesMap from "../../components/StatesMap";
import Button from "../../components/Button";
import { mapModes, stateCodeToName, views, viewToRoute } from "../../constants";
import "./styles.css";
import {
  Global,
  MapContainer,
  MapViewHeader,
  MapViewToggle,
  DesktopSchoolStateToggle,
} from "./styled.js";
import { MapProvider } from "react-map-gl";
import { filterSchoolsByState, isEmbedded } from "../../utils.js";
import schools from "../../data/schools";
import s4sGrantees from "../../data/s4s-grantees";
import meaGrantees from "../../data/mea-grantees";

function App({ mapMode, stateCode, enableS4sGrantees, enableMeaGrantees }) {
  const { view } = useParams();

  const fallbackMapView =
    mapMode === mapModes.STATE ? views.STATE_DISTRICTS : views.SCHOOLS;

  const defaultMapView = (view && viewToRoute[view]) || fallbackMapView;

  const navigate = useNavigate();
  const [mapView, setMapView] = useState(defaultMapView);
  const [schoolsData, setSchoolsData] = useState([]);
  const [embedded] = useState(isEmbedded);
  const stateName = stateCodeToName[stateCode];

  const hasS4sGrantees =
    enableS4sGrantees && filterSchoolsByState(s4sGrantees, stateCode).length > 0;
  const hasMeaGrantees =
    enableMeaGrantees && filterSchoolsByState(meaGrantees, stateCode).length > 0;

  useEffect(() => {
    const route = viewToRoute[mapView];
    if (mapMode === mapModes.STATE) {
      navigate(`/${stateCode.toLowerCase()}${route ? `/${route}` : ""}`);
      if (mapView === views.S4S_GRANTEES) {
        setSchoolsData(filterSchoolsByState([...s4sGrantees], stateCode));
      } else if (mapView === views.MEA_GRANTEES) {
        setSchoolsData(filterSchoolsByState([...meaGrantees], stateCode));
      } else {
        setSchoolsData(filterSchoolsByState([...schools], stateCode));
      }
    } else if(!mapMode) {
      navigate(`${route ? `/${route}` : ""}`);
    }
  }, [mapView, mapMode, navigate, stateCode]);

  const onViewChange = (view) => {
    setMapView(view);
  };

  const stateMap = (
    <StateMap
      stateName={stateName}
      stateCode={stateCode}
      schoolsData={schoolsData}
      onViewChange={onViewChange}
      enableS4sGrantees={hasS4sGrantees}
      enableMeaGrantees={hasMeaGrantees}
      mapView={mapView}
    />
  );

  const defaultMap = (
    <>
      {!embedded && (
        <MapViewHeader>
          <MapViewToggle>
            <Button
              type="light"
              isActive={mapView === views.SCHOOLS}
              onClick={() => onViewChange(views.SCHOOLS)}
            >
              School
            </Button>
            <Button
              type="light"
              isActive={mapView === views.STATES}
              onClick={() => onViewChange(views.STATES)}
            >
              State
            </Button>
          </MapViewToggle>
          <DesktopSchoolStateToggle
            options={[
              { value: views.SCHOOLS, label: "School" },
              { value: views.STATES, label: "State" },
            ]}
            value={mapView}
            onChange={onViewChange}
          />
        </MapViewHeader>
      )}
      {mapView === views.SCHOOLS && <SchoolsMap />}
      {mapView === views.STATES && <StatesMap />}
    </>
  );

  const getMap = () => {
    switch (mapMode) {
      case mapModes.STATE:
        return stateMap;
      default:
        return defaultMap;
    }
  };

  return (
    <MapProvider>
      <Global data-embedded={embedded}>
        <MapContainer>{getMap()}</MapContainer>
      </Global>
    </MapProvider>
  );
}

export default App;
